import {
  PRODUITS, FAMILLES, BOUTIQUES, REGLAGES, AVIS,
  type Produit, type Famille,
} from './mock-data';

/**
 * COUCHE DE LECTURE — le seul endroit à changer pour brancher l'API.
 *
 * Aujourd'hui : `mock-data.ts` (fichier unique, généré).
 * Demain : `/v1/catalogue`, `/v1/settings/public`, `/v1/fulfillment/stores`…
 * Les écrans n'importent jamais `mock-data` directement (sauf les textes éditoriaux),
 * ils passent par ces fonctions. Les règles de calcul (prix, arrhes, tolérance)
 * viennent **toutes** de `REGLAGES`, jamais d'un littéral dans un composant.
 */

/* ----------------------------------------------------------------- formats */

export const eur = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

export const eurSansCentimes = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

export const kg = (grammes: number) =>
  `${(grammes / 1000).toFixed(grammes % 1000 === 0 ? 0 : 2).replace('.', ',').replace(',00', '')} kg`;

/** Date ISO (`2026-10-12`) → français court (`12 oct. 2026`). Midi UTC : pas de décalage d'un jour. */
export const dateFr = (iso: string) => {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(d)
    .replace(/\u00a0/g, ' ');
};

const arrondi2 = (n: number) => Math.round(n * 100) / 100;
const auDessus = (n: number) => Math.ceil(n);

/* ---------------------------------------------------------------- produits */

export const produits = (): Produit[] => PRODUITS;
export const familles = (): Famille[] => FAMILLES;
export const famille = (slug: string) => FAMILLES.find(f => f.slug === slug);
export const produit = (slug: string) => PRODUITS.find(p => p.slug === slug);
export const produitsDe = (familleSlug: string) => PRODUITS.filter(p => p.famille === familleSlug);

export const estAuPoids = (p: Produit) => p.modele === 'AU_KG';

/** Prix pour un poids donné — ou pour un format choisi. */
export function prixPour(p: Produit, poidsG?: number, formatIndex?: number): number {
  if (p.modele === 'FORMATS') {
    const f = typeof formatIndex === 'number' ? p.formats[formatIndex] : p.formats[0];
    return f ? f.prix : 0;
  }
  if (p.modele === 'PRIX_FIXE' || p.modele === 'COLIS') return p.prixFixe ?? 0;
  if (!p.prixKg) return 0;
  const poids = poidsG ?? p.portion.poidsReferenceG ?? p.portion.minG;
  return arrondi2((poids / 1000) * p.prixKg);
}

/** Prix d'appel affiché sur une carte : la valeur et son unité, jamais l'un sans l'autre. */
export function prixAffiche(p: Produit): { valeur: string; unite: string } {
  if (p.modele === 'AU_KG' && p.prixKg) return { valeur: eur(p.prixKg), unite: '/ kg' };
  if (p.modele === 'FORMATS' && p.formats[0]) return { valeur: eur(p.formats[0].prix), unite: `les ${p.formats[0].libelle}` };
  return { valeur: eur(p.prixFixe ?? 0), unite: p.poidsG ? kg(p.poidsG) : '' };
}

/** Le poids proposé par défaut : celui d'une tablée de quatre, ramené aux bornes publiées. */
export function poidsParDefaut(p: Produit): number {
  if (p.portion.poidsReferenceG) return p.portion.poidsReferenceG;
  if (p.modele === 'FORMATS') return p.formats[0]?.poidsG ?? p.portion.minG;
  const cible = p.portion.gParPersonne * 4;
  const borne = Math.min(Math.max(cible, p.portion.minG), p.portion.maxG);
  return Math.round(borne / p.portion.pasG) * p.portion.pasG;
}

/** Estimation + fourchette de pesée, calculées avec la tolérance publiée. */
export function fourchette(p: Produit, poidsG: number) {
  const estimation = prixPour(p, poidsG);
  const t = REGLAGES.poids.tolerancePct / 100;
  return {
    estimation,
    bas: arrondi2(estimation * (1 - t)),
    haut: arrondi2(estimation * (1 + t)),
  };
}

/** Vrai si la pièce peut être retirée dans cette boutique (politique de rayon). */
export function disponibleEn(p: Produit, boutiqueId: string) {
  return p.disponibleEn.includes(boutiqueId);
}

export const boutiques = () => BOUTIQUES;
export const boutique = (id: string) => BOUTIQUES.find(b => b.id === id) ?? BOUTIQUES[0];

/* --------------------------------------------------------------- livraison */

export function fraisLivraison(sousTotal: number, mode: 'PICKUP' | 'DELIVERY'): number {
  if (mode === 'PICKUP') return 0;
  return sousTotal >= REGLAGES.livraison.seuilGratuite ? 0 : REGLAGES.livraison.fraisChronofresh;
}

export function manquePourLivraisonOfferte(sousTotal: number): number {
  return Math.max(0, REGLAGES.livraison.seuilGratuite - sousTotal);
}

/* ------------------------------------------------------------------ arrhes
 * Reproduit **à l'écran** la règle publiée pour annoncer un montant avant paiement.
 * Le serveur recalcule et reste seul juge ; ici, on ne fait qu'annoncer.
 * R3  (< 50 €)  : rien à payer, la commande est réservée.
 * R3b (50–120)  : 20 %, plancher 10 €, ou tout au comptoir.
 * R2  (> 120)   : le plus élevé de 30 % ou 40 €, plafonné.
 * R6  (livraison): 100 % en ligne.
 */
export interface Arhes {
  montant: number;
  regle: 'R3' | 'R3b' | 'R2' | 'R6';
  pourcentage: number | null;
  choixComptoir: boolean;
  explication: string;
}

export function arrhes(sousTotal: number, mode: 'PICKUP' | 'DELIVERY'): Arhes {
  const a = REGLAGES.arrhes;
  if (mode === 'DELIVERY') {
    return {
      montant: arrondi2(sousTotal), regle: 'R6', pourcentage: 100, choixComptoir: false,
      explication: 'La livraison réfrigérée se règle intégralement en ligne, arrhes comprises dans le montant.',
    };
  }
  if (sousTotal < (a.seuilLibre ?? 0)) {
    return {
      montant: 0, regle: 'R3', pourcentage: 0, choixComptoir: false,
      explication: `En dessous de ${eurSansCentimes(a.seuilLibre ?? 0)}, aucune arrhes n’est demandée : votre commande est simplement réservée.`,
    };
  }
  if (sousTotal <= (a.seuilHaut ?? 0)) {
    const brut = (sousTotal * (a.pctStandard ?? 0)) / 100;
    return {
      montant: auDessus(Math.max(brut, a.plancherStandard ?? 0)), regle: 'R3b', pourcentage: a.pctStandard,
      choixComptoir: true,
      explication: `Entre ${eurSansCentimes(a.seuilLibre ?? 0)} et ${eurSansCentimes(a.seuilHaut ?? 0)}, les arrhes sont de ${a.pctStandard} % — vous pouvez aussi tout régler au comptoir.`,
    };
  }
  const pct = sousTotal > (a.seuilHaut ?? 0) ? (a.pctMesure ?? 0) : 0;
  const montant = Math.max((sousTotal * pct) / 100, 40);
  return {
    montant: auDessus(Math.min(montant, 300)), regle: 'R2', pourcentage: pct, choixComptoir: false,
    explication: `Au-delà de ${eurSansCentimes(a.seuilHaut ?? 0)}, la pièce est préparée sur mesure : les arrhes sont renforcées et le solde se règle au retrait après pesée.`,
  };
}

/* --------------------------------------------------------------- recherche */

const normaliser = (t: string) =>
  t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export function rechercher(terme: string, limite?: number): Produit[] {
  const q = normaliser(terme.trim());
  if (q.length < 2) return [];
  const mots = q.split(/\s+/);
  return PRODUITS
    .map(p => {
      const foin = normaliser([p.nom, p.description, p.familleNom, p.race ?? '', p.labels.join(' '), p.tags.join(' ')].join(' '));
      const score = mots.reduce((n, m) => n + (normaliser(p.nom).includes(m) ? 3 : foin.includes(m) ? 1 : 0), 0);
      return { p, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limite ?? 999)
    .map(x => x.p);
}

/* ---------------------------------------------------------------- facettes
 * Les facettes sont **déduites du rayon consulté**, jamais d'une liste fixe :
 * on ne propose pas « Maturation » dans l'épicerie, ni « Pommes de terre » dans
 * l'agneau. Un groupe n'apparaît que s'il distingue au moins deux pièces.
 */
export interface OptionFacette {
  id: string;
  libelle: string;
  compte: number;
  predicat: (p: Produit) => boolean;
}
export interface GroupeFacettes {
  id: string;
  titre: string;
  options: OptionFacette[];
}

const TYPES: Array<{ id: string; libelle: string; tags: string[]; cuissons: string[] }> = [
  { id: 'griller', libelle: 'À griller', tags: ['barbecue', 'grillade'], cuissons: ['Plancha', 'Braise', 'Barbecue', 'Grill', 'Rôtissoire'] },
  { id: 'mijoter', libelle: 'À mijoter', tags: ['a_mijoter'], cuissons: ['Mijoté', 'Cocotte', 'Bouillon'] },
  { id: 'rotir', libelle: 'À rôtir', tags: ['dimanche'], cuissons: ['Four'] },
  { id: 'partager', libelle: 'À partager', tags: ['a_partager', 'signature'], cuissons: [] },
  { id: 'evenement', libelle: 'Pour un événement', tags: ['evenement', 'mechoui', 'colis'], cuissons: [] },
];

const typesDe = (p: Produit): string[] =>
  TYPES.filter(t =>
    p.tags.some(tag => t.tags.includes(tag))
    || (t.cuissons.length > 0 && p.cuisson.some(c => t.cuissons.some(x => c.toLowerCase().includes(x.toLowerCase())))),
  ).map(t => t.id);

export function facettes(familleSlug?: string | null): GroupeFacettes[] {
  const base = familleSlug ? produitsDe(familleSlug) : PRODUITS;
  const groupes: GroupeFacettes[] = [];

  const ajouter = (id: string, titre: string, options: Array<Omit<OptionFacette, 'compte'>>) => {
    const remplies = options
      .map(o => ({ ...o, compte: base.filter(o.predicat).length }))
      .filter(o => o.compte > 0);
    if (remplies.length >= 2) groupes.push({ id, titre, options: remplies });
  };

  /* Type d'usage : ce que le client veut faire de la pièce. */
  ajouter('type', 'Ce que vous voulez faire',
    TYPES.filter(t => base.some(p => typesDe(p).includes(t.id)))
      .map(t => ({ id: t.id, libelle: t.libelle, predicat: (p: Produit) => typesDe(p).includes(t.id) })));

  /* Race : seulement si plusieurs races cohabitent. */
  const races = [...new Set(base.map(p => p.race).filter((r): r is string => !!r))];
  if (races.length >= 2) {
    ajouter('race', 'Race', races.map(r => ({
      id: r, libelle: r, predicat: (p: Produit) => p.race === r,
    })));
  }

  /* Maturation : par paliers, et seulement sur les rayons qui maturent. */
  const maturations = base.map(p => p.maturationJours).filter((n): n is number => typeof n === 'number' && n > 0);
  if (maturations.length >= 2 && new Set(maturations).size >= 2) {
    const paliers = [
      { id: '21-27', libelle: '21 à 27 jours', min: 21, max: 27 },
      { id: '28-34', libelle: '28 à 34 jours', min: 28, max: 34 },
      { id: '35+', libelle: '35 jours et plus', min: 35, max: 999 },
    ];
    ajouter('maturation', 'Maturation', paliers.map(pal => ({
      id: pal.id, libelle: pal.libelle,
      predicat: (p: Produit) => (p.maturationJours ?? 0) >= pal.min && (p.maturationJours ?? 0) <= pal.max,
    })));
  }

  /* Labels : un par option, seulement ceux qui existent dans ce rayon. */
  const labels = [...new Set(base.flatMap(p => p.labels))];
  if (labels.length >= 2) {
    ajouter('label', 'Labels', labels.map(l => ({
      id: l, libelle: l, predicat: (p: Produit) => p.labels.includes(l),
    })));
  }

  /* Abattage maison : uniquement là où c'est un critère. */
  if (base.some(p => p.abattageMaison) && base.some(p => !p.abattageMaison)) {
    ajouter('provenance', 'Provenance', [
      { id: 'maison', libelle: 'Abattu à Sannois', predicat: (p: Produit) => p.abattageMaison },
      { id: 'eleveurs', libelle: 'Éleveurs partenaires', predicat: (p: Produit) => !p.abattageMaison },
    ]);
  }

  /* Format : pesé ou à la pièce. */
  if (base.some(p => p.modele === 'FORMATS') && base.some(p => p.modele !== 'FORMATS')) {
    ajouter('conditionnement', 'Conditionnement', [
      { id: 'formats', libelle: 'En formats pesés', predicat: (p: Produit) => p.modele === 'FORMATS' },
      { id: 'coupe', libelle: 'À la coupe', predicat: (p: Produit) => p.modele !== 'FORMATS' },
    ]);
  }

  /* Prix : trois tranches calculées sur le rayon, en prix au kilo ou prix ferme. */
  const prix = base.map(p => p.prixKg ?? p.prixFixe ?? 0).filter(n => n > 0);
  if (prix.length >= 3) {
    const min = Math.min(...prix);
    const max = Math.max(...prix);
    if (max - min > 1) {
      const pas = (max - min) / 3;
      const bornes = [min + pas, min + 2 * pas];
      ajouter('prix', 'Prix', [
        { id: 'bas', libelle: `Moins de ${eurSansCentimes(bornes[0])}`, predicat: (p: Produit) => (p.prixKg ?? p.prixFixe ?? 0) <= bornes[0] },
        { id: 'moyen', libelle: `De ${eurSansCentimes(bornes[0])} à ${eurSansCentimes(bornes[1])}`, predicat: (p: Produit) => { const v = p.prixKg ?? p.prixFixe ?? 0; return v > bornes[0] && v <= bornes[1]; } },
        { id: 'haut', libelle: `Plus de ${eurSansCentimes(bornes[1])}`, predicat: (p: Produit) => (p.prixKg ?? p.prixFixe ?? 0) > bornes[1] },
      ]);
    }
  }

  return groupes;
}

export type Selection = Record<string, string[]>;

export function filtrer(liste: Produit[], selection: Selection, groupes: GroupeFacettes[]): Produit[] {
  return liste.filter(p => Object.entries(selection).every(([groupeId, choisis]) => {
    if (!choisis.length) return true;
    const groupe = groupes.find(g => g.id === groupeId);
    if (!groupe) return true;
    /* Dans un groupe, les options se cumulent en OU ; entre groupes, en ET. */
    return choisis.some(id => groupe.options.find(o => o.id === id)?.predicat(p));
  }));
}

export const nbFiltres = (selection: Selection) =>
  Object.values(selection).reduce((n, l) => n + l.length, 0);

/* -------------------------------------------------------------------- tri */

export type Tri = 'recommande' | 'prix-asc' | 'prix-desc' | 'poids';
export const TRIS: Array<{ valeur: Tri; libelle: string }> = [
  { valeur: 'recommande', libelle: 'Recommandé pour vous' },
  { valeur: 'prix-asc', libelle: 'Prix au kilo croissant' },
  { valeur: 'prix-desc', libelle: 'Prix au kilo décroissant' },
  { valeur: 'poids', libelle: 'Poids conseillé croissant' },
];

export function trier(liste: Produit[], tri: Tri): Produit[] {
  const copie = [...liste];
  const prix = (p: Produit) => p.prixKg ?? p.prixFixe ?? 0;
  switch (tri) {
    case 'prix-asc': return copie.sort((a, b) => prix(a) - prix(b));
    case 'prix-desc': return copie.sort((a, b) => prix(b) - prix(a));
    case 'poids': return copie.sort((a, b) => poidsParDefaut(a) - poidsParDefaut(b));
    default: {
      /* « Recommandé » : les pièces de la maison d'abord, puis les plus commandées. */
      const rang = (p: Produit) =>
        (p.tags.includes('signature') ? -3 : 0) + (p.tags.includes('best_seller') ? -2 : 0) + (p.tags.includes('exception') ? 1 : 0);
      return copie.sort((a, b) => rang(a) - rang(b) || prix(b) - prix(a));
    }
  }
}

/* -------------------------------------------------------------- similaires */

export function produitsSimilaires(p: Produit, n = 4): Produit[] {
  const memeFamille = PRODUITS.filter(x => x.id !== p.id && x.famille === p.famille);
  const partage = PRODUITS.filter(x => x.id !== p.id && x.famille !== p.famille
    && (x.espece === p.espece || x.tags.some(t => p.tags.includes(t))));
  return [...memeFamille, ...partage].slice(0, n);
}

/* -------------------------------------------------------------------- avis */

export const avisPour = (slug: string) => AVIS.filter(a => a.produit === slug);
export const avis = () => AVIS;
export const noteMoyenne = (liste = AVIS) =>
  arrondi2(liste.reduce((n, a) => n + a.note, 0) / Math.max(1, liste.length));


/* --------------------------------------------------------------------------
 * NOMS ATTENDUS PAR LES ÉCRANS
 *
 * Les quatre fonctions ci-dessous portent le vocabulaire des pages (facettes,
 * créneaux, formats, signature). Ce sont des **façades** : aucune ne recalcule
 * une règle, toutes s'appuient sur les fonctions ci-dessus.
 * ------------------------------------------------------------------------ */

/** Un prix à la française, taux compris : « 5,5 % », jamais « 5.5 % ». */
export const pct = (taux: number, decimales = 1) =>
  `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: decimales }).format(taux)} %`;

/** Les facettes naissent du rayon affiché, jamais d'une liste fixe. */
export type GroupeFacette = GroupeFacettes;
export const groupesDeFiltres = (familleSlug?: string | null) => facettes(familleSlug);

/** Le format dont le poids est le plus proche de celui demandé. */
export function formatLePlusProche(p: Produit, poidsG: number) {
  if (!p.formats.length) return undefined;
  return p.formats.reduce((meilleur, f) =>
    Math.abs(f.poidsG - poidsG) < Math.abs(meilleur.poidsG - poidsG) ? f : meilleur,
  );
}

/** Les pièces de la maison, pour « vous aimerez aussi » et le compte. */
export function produitsSignature(n = 4): Produit[] {
  const rang = (p: Produit) =>
    (p.tags.includes('signature') ? 0 : 2) + (p.tags.includes('best_seller') ? 0 : 1);
  return [...PRODUITS].sort((a, b) => rang(a) - rang(b) || (b.prixKg ?? 0) - (a.prixKg ?? 0)).slice(0, n);
}

/**
 * Les prochains créneaux de retrait, calculés sur les réglages publiés :
 * jours d'ouverture, coupure de midi, délai de découpe. Aucune date en dur —
 * le 17 octobre 2026 ne doit pas devenir un créneau éternel.
 */
export function prochainsCreneaux(n = 4, depuis: Date = new Date()) {
  const ouverts = new Set(REGLAGES.livraison.joursExpedition.map(j => j.toLowerCase()));
  const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const sorties: Array<{ date: string; jour: string; heures: string }> = [];
  const curseur = new Date(depuis);
  curseur.setHours(0, 0, 0, 0);
  let garde = 0;
  while (sorties.length < n && garde < 21) {
    curseur.setDate(curseur.getDate() + 1);
    garde += 1;
    if (!ouverts.has(JOURS[curseur.getDay()])) continue;
    const jour = `${JOURS[curseur.getDay()]} ${curseur.getDate()} ${curseur.toLocaleDateString('fr-FR', { month: 'long' })}`;
    const iso = curseur.toISOString().slice(0, 10);
    for (const [debut, fin] of [['10h00', '10h30'], ['11h00', '11h30'], ['16h00', '16h30']]) {
      if (sorties.length >= n) break;
      sorties.push({ date: `${iso}·${debut}`, jour, heures: `${debut}–${fin}` });
    }
  }
  return sorties;
}
